import autolinker from 'autolinker'
import moment from 'moment'
import i18n from '@/i18n'
import { dayOfWeek } from '@/helpers/utils'
import { buildSystemMessage } from '../../helperMethods'
import { getMyUserId, getUser } from '../../store'

export const PARTS_REGEX = /(<@[0-9]+>)|(<@everyone>)|(<a.+?<\/a>)/

const STYLED_SYSTEM_EVENTS = [
	'BUBBLE_CREATED',
	'BUBBLE_JOIN',
	'BUBBLE_LEAVE',
	'BUBBLE_KICKED',
	'TITLE_UPDATE',
	'DROPIN_INVITED',
	'DM_CREATED',
	'TASK_ASSIGNED',
	'TASK_REASSIGNED',
	'TASK_DUEDATECHANGED',
	'TASK_CLAIMED',
	'TASK_COMPLETED',
	'TASK_UNCOMPLETED',
	'TASK_DELETED',
	'BUBBLE_ADDED_MANY',
	'VIDEOSESSION_STARTED',
	'THREAD_CREATED',
	'THREAD_CONVERTED',
]

export const isStyledSystemEvent = (message) =>
	STYLED_SYSTEM_EVENTS.includes(message?.systemevent)

export const isThreadCreatedEvent = (message) =>
	message?.systemevent === 'THREAD_CREATED' ||
	message?.systemevent === 'THREAD_CONVERTED'

export const isSystemLinkEvent = (message) =>
	['BUBBLE_JOIN', 'BUBBLE_KICKED', 'BUBBLE_ADDED_MANY'].includes(
		message?.systemevent
	)

export const systemMessage = (message) => buildSystemMessage(message)

export const systemMessagePrefix = (message) =>
	message?.systemevent === 'BUBBLE_JOIN' ? 'added' : 'removed'

// Task due/completed dates are "floating" — interpret in the user's local timezone.
export const dueDateDisplay = (date) => {
	const dateOnly = date?.substring(0, 10)
	const DOW = dayOfWeek(dateOnly)
	return ['TODAY', 'TOMORROW', 'YESTERDAY'].includes(DOW)
		? i18n.global.t(`common_${DOW.toLowerCase()}`).toLowerCase()
		: moment(dateOnly).format('LL')
}

export const isTaskPastDue = (task) => {
	if (!task || task.completed) return false
	return moment().isAfter(task.due?.substring(0, 10), 'day')
}

export const partType = {
	MENTION: 'mention',
	LINK_CARD: 'link_card',
	HYPERLINK: 'hyperlink',
	TEXT: 'text',
}

const getElementFromHTML = (string) => {
	const doc = new DOMParser().parseFromString(string, 'text/html')
	return doc.body.firstChild
}

export const parseMessageParts = (messageObj) => {
	const { message, messagetrans, resource, isTranslated, mentions } = messageObj
	let messageText = null
	if (isTranslated && messagetrans) {
		// if translation available, use that
		messageText = messagetrans.translation
	} else {
		// prevent XSS by replacing any existing anchor tags
		const linkless = message.replace(/<a/g, '{{{LINK_TAG}}}')
		// check the remaining text for links
		messageText = autolinker.link(linkless, {
			phone: false,
			stripPrefix: false,
			decodePercentEncoding: false,
		})
	}
	// split message into parts by type and filter
	// out any junk parts from the regex
	const parts = messageText.split(PARTS_REGEX).filter((p) => p)
	return parts
		.map((text, index) => {
			// identify which parts are of which type
			if (text.startsWith('<@')) {
				const id = text.replace(/[<>@]/g, '')
				if (id === 'everyone') {
					return { type: partType.MENTION, userId: id, isMe: true }
				}
				if (isNaN(parseInt(id))) {
					// only valid ids should become mentions
					return { type: partType.TEXT, text }
				}
				if (!mentions.includes(parseInt(id))) {
					// id does not correspond to a user w/ mentions in this chat
					return { type: partType.MENTION, userId: id }
				}
				return {
					type: partType.MENTION,
					userId: id,
					user: getUser(id),
					isMe: id == getMyUserId(),
				}
			}
			if (text.startsWith('<a ')) {
				const atStart = index === 0
				const atEnd = !atStart && index === parts.length - 1
				if (resource && (atStart || atEnd)) {
					return {
						// if message contains a url at the start or end, should show card
						type: partType.LINK_CARD,
						atStart,
						atEnd,
					}
				}
				const a = getElementFromHTML(text)
				return {
					type: partType.HYPERLINK,
					href: a.getAttribute('href'),
					text: a.innerText,
				}
			}
			return {
				type: partType.TEXT,
				// put any replaced links back in their place
				text: text.replace(/{{{LINK_TAG}}}/g, '<a'),
			}
		})
		.map((part, index, array) => {
			if (part.type === partType.TEXT) {
				const prevPart = array[index - 1]
				const nextPart = array[index + 1]
				if (prevPart?.type === partType.LINK_CARD) {
					// if there is a LINK_CARD before this text, then remove leading newline
					part.text = part.text.replace(/^\n+/g, '')
				} else if (nextPart?.type === partType.LINK_CARD) {
					// if there is a LINK_CARD after this text, then remove trailing newline
					part.text = part.text.replace(/\n+$/g, '')
				}
			}
			return part
		})
}
